import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || await navigator.serviceWorker.register("/service-worker.js");
}

export default function PushToggle() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState("");

  useEffect(() => {
    const ok = ("serviceWorker" in navigator) && ("PushManager" in window)
                && ("Notification" in window);
    setSupported(ok);
    if (!ok) return;
    (async () => {
      try {
        const cfg = await api.get("/push/config");
        setPublicKey(cfg.data?.public_key || "");
        const reg = await getRegistration();
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch {}
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permissão negada pelo navegador"); return; }
      const reg = await getRegistration();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notificações push ativadas");
    } catch (e) {
      toast.error("Falha ao ativar: " + (e?.message || "erro"));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notificações push desativadas");
    } catch (e) {
      toast.error("Falha ao desativar");
    } finally { setBusy(false); }
  };

  const test = async () => {
    try { const r = await api.post("/push/test"); toast.success(`Push enviado (${r.data?.sent || 0} device(s))`); }
    catch { toast.error("Falha no teste"); }
  };

  if (!supported) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-2">
        <BellOff className="w-4 h-4"/> Seu navegador não suporta push notifications.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={subscribed ? disable : enable}
        disabled={busy || !publicKey}
        data-testid="push-toggle-btn"
        className={`h-9 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-2 transition ${
          subscribed
            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
      >
        {subscribed ? <Bell className="w-3.5 h-3.5"/> : <BellOff className="w-3.5 h-3.5"/>}
        {subscribed ? "Push ativado neste dispositivo" : "Ativar push notifications"}
      </button>
      {subscribed && (
        <button onClick={test} data-testid="push-test-btn"
          className="h-9 px-3 rounded-lg text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50">
          Enviar teste
        </button>
      )}
    </div>
  );
}
